import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelectedPlayers } from '../contexts/SelectedPlayersContext';
import { saveWorkoutCompletion } from '../firebase';
import Confetti from 'react-confetti';
import { getVersionedAudioUrl } from '../utils/audioUrl';
import './DrillScreen.css';

const DrillScreen = ({ addPoints }) => {
  const { drillId, sport: urlSport } = useParams();
  const navigate = useNavigate();
  const { selectedPlayersData } = useSelectedPlayers();

  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [showIntroScreen, setShowIntroScreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [currentExerciseData, setCurrentExerciseData] = useState(null);

  // Audio ref
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // Import drill data
  const [drillData, setDrillData] = useState(null);

  // Derive sport from drill data
  const sport = drillData ? drillData.sport : urlSport;

  // Audio file paths
  const getAudioPath = (type, exerciseName = null) => {
    let path = null;

    // Check if this is a partner drill (drillId >= 1501)
    const isPartnerDrill = drillId >= 1501;

    // Sport-specific drill audio folder mapping
    const regularMap = {
      soccer: 'latest-generated-drills-audio',
      basketball: 'latest-generated-basketball-drills-audio',
      baseball: 'latest-generated-baseball-drills-audio',
      karate: 'latest-generated-karate-drills-audio',
      taekwondo: 'latest-generated-taekwondo-drills-audio',
      volleyball: 'latest-generated-volleyball-drills-audio',
      'movement-coordination':
        'latest-generated-movement-coordination-drills-audio',
      'martial-arts': 'latest-generated-martial-arts-drills-audio',
      martial_arts: 'latest-generated-martial-arts-drills-audio',
      dance: 'latest-generated-dance-drills-audio',
      ballet: 'latest-generated-ballet-drills-audio',
      boxing: 'latest-generated-boxing-drills-audio',
      yoga: 'latest-generated-yoga-drills-audio',
      'running-technique': 'latest-generated-running-technique-drills-audio',
    };

    const audioFolder = isPartnerDrill
      ? 'latest-generated-partner-drills-audio'
      : regularMap[sport] || 'latest-generated-drills-audio';

    if (type === 'intro') {
      // Drill intro: /latest-generated-ballet-drills-audio/ballet_drill_2190_intro.mp3
      path = `/${audioFolder}/${sport}_${
        isPartnerDrill ? 'partner_drill' : 'drill'
      }_${drillId}_intro.mp3`;
    } else if (type === 'conclusion') {
      // Drill conclusion: /latest-generated-ballet-drills-audio/ballet_drill_2190_conclusion.mp3
      path = `/${audioFolder}/${sport}_${
        isPartnerDrill ? 'partner_drill' : 'drill'
      }_${drillId}_conclusion.mp3`;
    } else if (type === 'exercise' && exerciseName) {
      if (isPartnerDrill) {
        // Partner drill instructions: /latest-generated-partner-drills-audio/soccer_partner_drill_1501_instructions.mp3
        path = `/${audioFolder}/${sport}_partner_drill_${drillId}_instructions.mp3`;
      } else {
        // Exercise audio: /latest-generated-exercises-audio/fastFeet.mp3
        const exerciseFileName = convertExerciseNameToFileName(exerciseName);
        path = `/latest-generated-exercises-audio/${exerciseFileName}.mp3`;
      }
    } else if (type === 'exerciseIntro' && exerciseName) {
      if (isPartnerDrill) {
        // Partner drills don't have separate exercise intros, use the main instructions
        path = `/${audioFolder}/${sport}_partner_drill_${drillId}_instructions.mp3`;
      } else {
        // Exercise intro audio: /latest-generated-exercises-audio/fastFeet_intro.mp3
        const exerciseFileName = convertExerciseNameToFileName(exerciseName);
        path = `/latest-generated-exercises-audio/${exerciseFileName}_intro.mp3`;
      }
    }

    return path;
  };

  // Convert exercise name to audio file name
  const convertExerciseNameToFileName = (exerciseName) => {
    // Map exercise names to their audio file names
    const exerciseNameMap = {
      'Fast Feet': 'fastFeet',
      'High Knees': 'highKnees',
      'Mountain Climbers': 'mountainClimbers',
      'Star Jumps': 'starJumps',
      'Skater Hops': 'skaterHops',
      'One Leg Stand': 'oneLegStand',
      'Side Lunges': 'sideLunges',
      'Big Arm Swings': 'bigArmSwings',
      'Rainbow Reaches': 'rainbowReaches',
      'Frog Jumps': 'frogJumps',
      'Mini Squats': 'miniSquats',
      Burpees: 'burpees',
      'Tiger Pounces': 'tigerPounces',
      'Bear Crawl': 'bearCrawl',
      'Crab Walk': 'crabWalk',
      'Zebra Gallops': 'zebraGallops',
      'Rabbit Hops': 'rabbitHops',
      Plank: 'plank',
      'Push-ups': 'pushUps',
      'Superman Holds': 'supermanHolds',
      'Wall Sits': 'wallSits',
      'Bridge Holds': 'bridgeHolds',
      'Upper Cuts': 'upperCuts',
      'Shoulder Taps': 'shoulderTaps',
      'Tricep Dips': 'tricepDips',
      'Inchworm Walk': 'inchwormWalk',
      'Arm Circles': 'armCircles',
      'Jumping Jacks': 'jumpingJacks',
    };

    return (
      exerciseNameMap[exerciseName] ||
      exerciseName.toLowerCase().replace(/\s+/g, '')
    );
  };

  // Play audio file
  const playAudio = (audioPath, onEnd) => {
    if (!audioPath) return;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Add timestamp versioning for automatic cache invalidation
    const versionedAudioPath = getVersionedAudioUrl(audioPath);
    const audio = new Audio(versionedAudioPath);

    // Track audio loading performance
    const startTime = performance.now();
    console.log('🎵 Starting audio load:', versionedAudioPath);

    audio.addEventListener('canplaythrough', () => {
      const loadTime = performance.now() - startTime;
      console.log('🎵 Audio loaded in:', loadTime.toFixed(2), 'ms');

      // Estimate if it was cached (very fast loads are usually cached)
      if (loadTime < 100) {
        console.log('✅ Likely loaded from cache');
      } else {
        console.log('📥 Likely downloaded from server');
      }
    });
    audioRef.current = audio;
    setIsAudioPlaying(true);

    audio.addEventListener('ended', () => {
      setIsAudioPlaying(false);
      if (onEnd) onEnd();
    });

    audio.addEventListener('error', (e) => {
      console.error('Audio error:', e);
      setIsAudioPlaying(false);
      if (onEnd) onEnd();
    });

    audio.play().catch((error) => {
      console.error('Error playing audio:', error);
      setIsAudioPlaying(false);
      if (onEnd) onEnd();
    });
  };

  // Stop audio
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsAudioPlaying(false);
  };

  useEffect(() => {
    // Import drill data dynamically
    import('../data/sessions_drills.js').then((module) => {
      const drill = module.default.find((d) => d.drillId === parseInt(drillId));
      if (drill) {
        setDrillData(drill);
        setTimeLeft(drill.exercises[0]?.duration || 30);
      }
    });

    // Cleanup function
    return () => {
      stopAudio();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [drillId]);

  useEffect(() => {
    let interval = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && isActive) {
      handleExerciseComplete();
    }
    return () => clearInterval(interval);
  }, [isActive, timeLeft]);

  const startTimedInstructions = (exerciseIndex = null) => {
    // Use provided exerciseIndex or fall back to currentExerciseIndex
    const targetExerciseIndex =
      exerciseIndex !== null ? exerciseIndex : currentExerciseIndex;

    // Get the exercise data for the target index
    const targetExercise = drillData.exercises[targetExerciseIndex];

    if (!targetExercise) {
      console.error(
        '❌ No exercise data found for index:',
        targetExerciseIndex
      );
      return;
    }

    // Set the current exercise data for display
    setCurrentExerciseData(targetExercise);
    setTimeLeft(targetExercise.duration || 30);
    setCurrentTime(0);

    // Check if this is a partner drill
    const isPartnerDrill = drillId >= 1501;

    if (isPartnerDrill) {
      // For partner drills, play intro then instructions directly

      const introPath = getAudioPath('intro');

      if (introPath) {
        // Start timer when intro begins
        const startTime = Date.now();
        timerRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setCurrentTime(elapsed);
        }, 1000);

        // Play intro first
        playAudio(introPath, () => {
          // When intro ends, play the instructions audio

          const instructionsPath = getAudioPath(
            'exercise',
            targetExercise.name
          );

          if (instructionsPath) {
            playAudio(instructionsPath, () => {
              // When instructions end, complete the drill
              handleNext();
            });
          } else {
            // Fallback: use exercise duration
            setTimeout(() => {
              handleNext();
            }, (targetExercise.duration || 30) * 1000);
          }
        });
      } else {
        // No intro file, go straight to instructions
        const instructionsPath = getAudioPath('exercise', targetExercise.name);
        if (instructionsPath) {
          playAudio(instructionsPath, () => {
            handleNext();
          });
        } else {
          // Fallback: use exercise duration
          setTimeout(() => {
            handleNext();
          }, (targetExercise.duration || 30) * 1000);
        }
      }
    } else {
      // Regular drill logic (existing code)

      const exerciseIntroPath = getAudioPath(
        'exerciseIntro',
        targetExercise.name
      );

      if (exerciseIntroPath) {
        // Start timer when intro begins
        const startTime = Date.now();
        timerRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setCurrentTime(elapsed);
        }, 1000);

        // Play intro first
        playAudio(exerciseIntroPath, () => {
          // When intro ends, play the main exercise audio

          const exerciseAudioPath = getAudioPath(
            'exercise',
            targetExercise.name
          );

          if (exerciseAudioPath) {
            playAudio(exerciseAudioPath, () => {
              // When main exercise audio ends, move to next exercise
              handleNext();
            });
          } else {
            // Fallback: use exercise duration
            setTimeout(() => {
              handleNext();
            }, (targetExercise.duration || 30) * 1000);
          }
        });
      } else {
        // No intro file, go straight to main exercise audio
        const exerciseAudioPath = getAudioPath('exercise', targetExercise.name);
        if (exerciseAudioPath) {
          playAudio(exerciseAudioPath, () => {
            handleNext();
          });
        } else {
          // Fallback: use exercise duration
          setTimeout(() => {
            handleNext();
          }, (targetExercise.duration || 30) * 1000);
        }
      }
    }
  };

  const handleNext = () => {
    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const nextExercise = currentExerciseIndex + 1;

    if (nextExercise < drillData.exercises.length) {
      // Update state
      setCurrentExerciseIndex(nextExercise);
      setCurrentTime(0);

      // Start the next exercise after a short delay
      setTimeout(() => {
        startTimedInstructions(nextExercise);
      }, 200);
    } else {
      // Drill complete
      setIsCompleted(true);
      setShowConfetti(true);
      stopAudio();

      // Play conclusion audio
      const conclusionAudioPath = getAudioPath('conclusion');

      if (conclusionAudioPath) {
        playAudio(conclusionAudioPath);
      }

      // Award points to selected players
      if (selectedPlayersData.length > 0) {
        const pointsPerPlayer = drillData.points;

        for (const player of selectedPlayersData) {
          addPoints('player', {
            playerId: player.playerId,
            points: pointsPerPlayer,
            workoutId: drillData.drillId,
            workoutData: drillData,
          });
        }

        // Save drill completion
        saveWorkoutCompletion({
          workoutId: drillData.drillId,
          workoutData: drillData,
          players: selectedPlayersData,
          points: pointsPerPlayer,
        });
      }

      // Hide confetti after 5 seconds
      setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
    }
  };

  const startDrillFlow = () => {
    setShowStartScreen(false);
    setShowIntroScreen(true);

    // Play drill intro audio
    const introAudioPath = getAudioPath('intro');

    if (introAudioPath) {
      playAudio(introAudioPath, () => {
        // When intro audio ends, start the first exercise
        setShowIntroScreen(false);
        setShowStartScreen(false);
        setIsActive(true);
        startTimedInstructions();
      });
    } else {
      // No intro audio, start directly
      setShowIntroScreen(false);
      setShowStartScreen(false);
      setIsActive(true);
      startTimedInstructions();
    }
  };

  const handleStart = () => {
    startDrillFlow();
  };

  const handleExerciseComplete = () => {
    if (currentExerciseIndex < drillData.exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      const nextExercise = drillData.exercises[currentExerciseIndex + 1];
      setTimeLeft(nextExercise.duration);
    } else {
      handleDrillComplete();
    }
  };

  const handleDrillComplete = async () => {
    setIsActive(false);
    setIsCompleted(true);
    setShowConfetti(true);

    // Award points to selected players
    if (selectedPlayersData.length > 0) {
      const pointsPerPlayer = drillData.points;

      for (const player of selectedPlayersData) {
        await addPoints('player', {
          playerId: player.playerId,
          points: pointsPerPlayer,
          workoutId: drillData.drillId,
          workoutData: drillData,
        });
      }

      // Save drill completion
      await saveWorkoutCompletion({
        workoutId: drillData.drillId,
        workoutData: drillData,
        players: selectedPlayersData,
        points: pointsPerPlayer,
      });
    }

    // Hide confetti after 5 seconds
    setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
  };

  const handleBackToSkills = () => {
    stopAudio();
    navigate(`/skills/sports/${sport}`);
  };

  const handleViewLeaderboard = () => {
    navigate('/leaderboard');
  };

  if (!drillData) {
    return <div>Loading drill...</div>;
  }

  const currentExercise = drillData.exercises[currentExerciseIndex];

  if (isCompleted) {
    return (
      <div className='drill-completion-screen'>
        {showConfetti && <Confetti />}
        <div className='completion-content'>
          <h1>You did it!</h1>
          <p>🎉 {drillData.title} Complete! 🎉</p>
          <p>You completed the {drillData.title} drill!</p>

          {selectedPlayersData.map((player) => (
            <div key={player.playerId} className='player-points'>
              {player.emoji} {player.playerName} {drillData.points} Points
            </div>
          ))}

          <p className='completion-message'>{drillData.completion}</p>

          <div className='completion-actions'>
            <button onClick={handleViewLeaderboard} className='action-button'>
              View Leaderboard
            </button>
            <button onClick={handleBackToSkills} className='action-button'>
              Back to Skills
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='drill-screen'>
      <div className='drill-header'>
        <h1>{drillData.title}</h1>
        <p>{drillData.description}</p>
        <div className='drill-info'>
          <span>
            Exercise {currentExerciseIndex + 1} of {drillData.exercises.length}
          </span>
          <span>{drillData.difficulty}</span>
          <span>{drillData.points} points</span>
          {drillData.requiresPartner && (
            <span className='partner-required'>👥 Partner Required</span>
          )}
          {drillData.minPlayers > 1 && (
            <span className='min-players'>
              👥 Min {drillData.minPlayers} Players
            </span>
          )}
        </div>
        {drillData.requiresPartner && selectedPlayersData.length < 2 && (
          <div className='partner-warning'>
            ⚠️ This drill requires a partner! Add another player to continue.
          </div>
        )}
      </div>

      <div className='exercise-section'>
        <h2>{currentExercise.title}</h2>
        <p>{currentExercise.description}</p>

        <div className='timer-section'>
          {!isActive ? (
            <button onClick={handleStart} className='start-button'>
              Start Exercise
            </button>
          ) : (
            <div className='timer'>
              <div className='time-display'>{timeLeft}</div>
              <div className='time-label'>seconds remaining</div>
            </div>
          )}
        </div>

        <div className='exercise-instructions'>
          <h3>Instructions:</h3>
          <p>{currentExercise.instructions}</p>
          <div className='equipment'>
            <strong>Equipment needed:</strong> {currentExercise.equipment}
          </div>
        </div>
      </div>

      <div className='progress-section'>
        <div className='progress-bar'>
          {drillData.exercises.map((exercise, index) => (
            <div
              key={index}
              className={`progress-step ${
                index < currentExerciseIndex
                  ? 'completed'
                  : index === currentExerciseIndex
                  ? 'current'
                  : 'upcoming'
              }`}
            >
              {index + 1}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DrillScreen;
